<template>
	<n-button
		:loading
		:disabled
		:size
		:circle
		:icon-placement
		:focusable
		:text
		:tag
		:attr-type
		:type="naiveType"
		:color
		:class="[
			`jm-button-${type || 'default'}`,
			disabled && 'jm-button-disabled',
			text && 'jm-button-text',
			jmLoading && 'jm-button-loading',
			!label && !$slots.default && 'jm-button-no-content'
		]"
	>
		<slot>{{ label }}</slot>
		<template v-if="$slots.icon || iconName" #icon>
			<slot name="icon">
				<Icon
					v-if="iconName"
					:name="iconName"
					:size="iconSize"
					:color="iconColor"
					:bg-color="iconBgColor"
					:border-radius="iconBorderRadius"
					:depth="iconDepth"
					:bg-size="iconBgSize"
				/>
			</slot>
		</template>
	</n-button>
</template>

<script lang="ts" setup>
import type { ButtonSize, ButtonType } from "naive-ui"
import { NButton } from "naive-ui"
import { computed, ref, watch } from "vue"
import Icon from "@/components/common/Icon.vue"

const props = defineProps<{
	loading?: boolean
	disabled?: boolean
	size?: ButtonSize
	type?: "primary" | "secondary" | "default"
	circle?: boolean
	text?: boolean
	focusable?: boolean
	tag?: keyof HTMLElementTagNameMap
	attrType?: "button" | "submit" | "reset"
	label?: string
	iconPlacement?: "left" | "right"
	iconName?: string
	iconSize?: number
	iconColor?: string
	iconBgColor?: string
	iconBorderRadius?: number
	iconDepth?: 1 | 2 | 3 | 4 | 5
	iconBgSize?: number
}>()

const naiveType = computed<ButtonType>(() => {
	if (props.disabled) {
		return "default"
	}

	switch (props.type) {
		case "primary":
			return "primary"
		default:
			return "default"
	}
})

const color = computed<string | undefined>(() => {
	if (!props.disabled && (!props.type || props.type === "default")) {
		return "rgba(25, 25, 33, 1)"
	}

	return undefined
})

// used to preserve the loading icon size during the loading-leave transition
const jmLoading = ref(false)
let jmLoadingTimeout: NodeJS.Timeout | null = null
watch(
	() => props.loading,
	newVal => {
		jmLoadingTimeout && clearTimeout(jmLoadingTimeout)

		if (newVal) {
			jmLoading.value = true
		} else {
			jmLoadingTimeout = setTimeout(() => {
				jmLoading.value = false
			}, 500)
		}
	},
	{ immediate: true }
)
</script>

<style lang="scss" scoped>
.n-button {
	&.jm-button-default {
		&:hover:not(.n-button--disabled):not(.n-button--loading) {
			opacity: 0.95;
		}
	}

	&.jm-button-primary {
		--n-color-hover: var(--primary-700-color);
		--n-border-hover: 1px solid var(--primary-700-color);
	}

	&.jm-button-secondary {
		--n-color: var(--grey-50-color);
		--n-color-hover: var(--grey-100-color);
		--n-text-color-hover: var(--grey-900-color);
		--n-border-hover: 1px solid var(--grey-200-color);
	}

	&.jm-button- {
		&primary,
		&secondary,
		&default {
			--n-opacity-disabled: 1;
			--n-text-color-disabled: var(--grey-500-color);
		}
	}

	&.jm-button- {
		&primary,
		&default {
			&.n-button--disabled {
				--n-color-disabled: var(--grey-100-color);
			}
		}
	}

	&.jm-button-text {
		--n-color-hover: transparent;
		--n-text-color-hover: var(--primary-500-color);

		&.jm-button-disabled {
			--n-color-disabled: transparent;
			--n-opacity-disabled: 0.5;
		}

		&.jm-button-loading {
			--n-icon-size: 8px;
		}
	}

	&.jm-button-no-content {
		width: var(--n-height);

		:deep(.n-button__icon) {
			margin-right: 0;
			margin-left: 0;
		}
	}
}
</style>
